import {
  ClearOutlined,
  FileExcelOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Input,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
  Select,
} from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useRef, useState } from 'react';
import { WithdrawalExcelService } from '../../utils/excel/withdrawalExcelService';
import WithdrawalTableActions from './WithdrawalTableActions';

dayjs.extend(utc);

interface WithdrawalTableProps {
  transactions: any[]; // All filtered transactions
  loading: boolean;
  onRefresh: () => void;
  onFilterChange: (filters: any) => void;
  formatCurrency: (amount: number) => string;
  formatCardNumber: (cardNumber?: string) => string;
  selectedMonth: number;
  selectedYear: number;
  isAdmin: boolean;
  employeeId?: number;
  onOptimisticUpdate?: (transactionId: number, newStatus: string) => void; // Thêm prop mới
}

const { Text } = Typography;
const { Option } = Select;

const WithdrawalTable: React.FC<WithdrawalTableProps> = ({
  transactions,
  loading,
  onRefresh,
  onFilterChange,
  formatCurrency,
  formatCardNumber,
  selectedMonth,
  selectedYear,
  isAdmin,
  employeeId,
  onOptimisticUpdate,
}) => {
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const tableRef = useRef<any>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentFilteredData, setCurrentFilteredData] = useState<any[]>([]);

  // Clear all filters (KHÔNG reset selectedRowKeys)
  const clearAllFilters = () => {
    setFilteredInfo({});
    setSearchText('');
    setSearchedColumn('');
    setCurrentFilteredData([...transactions]);
    // Reset filters in parent component
    handleColumnFilterChange({});
  };

  // Handle filter changes from table columns
  const handleColumnFilterChange = (changedFilters: Record<string, any>) => {
    setFilteredInfo(changedFilters);

    // Convert Antd filters to frontend filter format
    const frontendFilters: any = {};

    // Convert filter values to appropriate format for frontend filtering
    if (
      changedFilters?.transactionDate &&
      changedFilters.transactionDate.length > 0
    ) {
      frontendFilters.transactionDate = changedFilters.transactionDate; // Array of day numbers
    }
    if (changedFilters?.creditCard && changedFilters.creditCard.length > 0) {
      frontendFilters.creditCard = changedFilters.creditCard[0]; // Search string
    }
    if (changedFilters?.employee && changedFilters.employee.length > 0) {
      frontendFilters.employee = changedFilters.employee; // Array of employee names
    }
    if (
      changedFilters?.posDeviceCode &&
      changedFilters.posDeviceCode.length > 0
    ) {
      frontendFilters.posDeviceCode = changedFilters.posDeviceCode; // Array of device codes
    }
    if (changedFilters?.amount && changedFilters.amount.length > 0) {
      frontendFilters.amount = changedFilters.amount[0]; // Search string
    }
    if (changedFilters?.feeApplied && changedFilters.feeApplied.length > 0) {
      frontendFilters.feeApplied = changedFilters.feeApplied[0]; // Search string
    }
    if (changedFilters?.balance && changedFilters.balance.length > 0) {
      frontendFilters.balance = changedFilters.balance[0]; // Search string
    }
    if (changedFilters?.status && changedFilters.status.length > 0) {
      frontendFilters.status = changedFilters.status; // Array of status values
    }

    // Call frontend filter handler
    onFilterChange(frontendFilters);
  };

  // Table onChange: handle only filtering (pagination is handled by Antd)
  const handleTableChange: TableProps<any>['onChange'] = (
    paginationParams,
    filters,
    sorter,
    extra
  ) => {
    // Handle filtering
    handleColumnFilterChange(filters || {});

    if (extra && extra.currentDataSource) {
      setCurrentFilteredData([...extra.currentDataSource]);
    }
  };

  // Get column search props for transaction date with day filter
  const getTransactionDateFilterProps = () => {
    // Tạo danh sách các ngày trong tháng hiện tại
    const daysInMonth = dayjs(
      `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`
    ).daysInMonth();
    const dayOptions = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Select
            mode="multiple"
            placeholder="Chọn ngày trong tháng"
            value={selectedKeys}
            onChange={(values) => setSelectedKeys(values)}
            style={{ width: '100%', marginBottom: 8 }}
            maxTagCount="responsive"
          >
            {dayOptions.map((day) => (
              <Option key={day} value={day}>
                Ngày {day}
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setSearchedColumn('transactionDate');
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        const transactionDate = dayjs(record.transactionDate);
        const dayOfMonth = transactionDate.date();
        return value === dayOfMonth;
      },
      filteredValue: filteredInfo['transactionDate'] || null,
    };
  };

  // Get column search props
  const getColumnSearchProps = (
    dataIndex: string,
    placeholder: string = 'Tìm kiếm...'
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setSearchText('');
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value: any, record: any) => {
      const recordValue = getNestedValue(record, dataIndex);
      return recordValue
        ? recordValue.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
    filteredValue: filteredInfo[dataIndex] || null,
  });

  // Get nested value from object
  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Get status tag
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Tag color="success">Đã xác nhận</Tag>;
      case 'PENDING':
        return <Tag color="warning">Chờ xác nhận</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Get return to company tag
  const getReturnToCompanyTag = (returnToCompany: boolean) => {
    return returnToCompany ? (
      <Tag color="blue">Về công ty</Tag>
    ) : (
      <Tag color="orange">Về nhân viên</Tag>
    );
  };

  // Table columns
  const columns: ColumnsType<any> = [
    {
      title: 'Ngày giao dịch',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 140,
      render: (date: string, record: any) => (
        <div>
          <div className="font-medium">{dayjs(date).format('DD-MM-YYYY')}</div>
          <div className="text-xs text-gray-500">
            {record.createdAt
              ? dayjs(record.createdAt).format('HH:mm')
              : '--:--'}
          </div>
        </div>
      ),
      ...getTransactionDateFilterProps(),
    },
    {
      title: 'Thẻ tín dụng',
      key: 'creditCard',
      width: 180,
      render: (_, record) => (
        <div>
          <div className="font-mono font-medium">
            {formatCardNumber(
              record.creditCard?.cardNumber || record.cardNumber
            )}
          </div>
          <div className="text-sm text-gray-500">
            {record.creditCard?.cardHolderName ||
              record.cardHolderName ||
              'Chưa có tên'}
          </div>
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo 4 số cuối hoặc tên chủ thẻ..."
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
              setSearchText(selectedKeys[0]);
              setSearchedColumn('creditCard');
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setSearchText(selectedKeys[0]);
                setSearchedColumn('creditCard');
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSearchText('');
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        const cardNumber =
          record.creditCard?.cardNumber || record.cardNumber || '';
        const cardHolderName =
          record.creditCard?.cardHolderName || record.cardHolderName || '';
        const lastFourDigits = cardNumber.slice(-4);

        return (
          lastFourDigits.toLowerCase().includes(value.toLowerCase()) ||
          cardHolderName.toLowerCase().includes(value.toLowerCase())
        );
      },
      filteredValue: filteredInfo.creditCard || null,
    },
    {
      title: 'Nhân viên thực hiện',
      dataIndex: 'employee',
      key: 'employee',
      width: 150,
      render: (_, record) => {
        // Xử lý dữ liệu employee từ TransactionResponseDto
        const employee = record.employee;

        if (employee) {
          return (
            <div>
              <div className="font-medium">
                {employee.name || 'Chưa có tên'}
              </div>
            </div>
          );
        }

        // Fallback cho trường hợp không có thông tin employee
        return (
          <div>
            <div className="font-medium text-gray-400">Chưa có thông tin</div>
            <div className="text-sm text-gray-400">N/A</div>
          </div>
        );
      },
      filters: transactions
        .map((record: any) => record.employee?.name)
        .filter(Boolean)
        .filter(
          (value: any, index: any, self: any) => self.indexOf(value) === index
        )
        .map((value: any) => ({ text: value, value })),
      filteredValue: filteredInfo.employee || null,
      onFilter: (value: any, record: any) => {
        const employeeName = record.employee?.name || '';
        return employeeName === value;
      },
    },
    {
      title: 'Thiết bị POS',
      dataIndex: 'posDeviceCode',
      key: 'posDeviceCode',
      width: 130,
      render: (_, record) => (
        <div>
          <div className="font-medium">
            {record.posDevice?.deviceCode || record.posDeviceCode || 'N/A'}
          </div>
        </div>
      ),
      filters: transactions
        .map(
          (record: any) => record.posDevice?.deviceCode || record.posDeviceCode
        )
        .filter(Boolean)
        .filter(
          (value: any, index: any, self: any) => self.indexOf(value) === index
        )
        .map((value: any) => ({ text: value, value })),
      filteredValue: filteredInfo.posDeviceCode || null,
      onFilter: (value: any, record: any) => {
        const deviceCode =
          record.posDevice?.deviceCode || record.posDeviceCode || '';
        return deviceCode === value;
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      render: (amount: number) => (
        <div className="text-right">
          <div className="font-bold text-green-600">
            {formatCurrency(amount)}
          </div>
        </div>
      ),
      ...getColumnSearchProps('amount', 'Tìm số tiền...'),
      filteredValue: filteredInfo.amount || null,
    },
    {
      title: 'Phí POS',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 100,
      render: (fee?: number) => (
        <div className="text-right">
          {fee ? (
            <span className="text-orange-600">{fee}%</span>
          ) : (
            <span className="text-gray-400">0%</span>
          )}
        </div>
      ),
      ...getColumnSearchProps('feeApplied', 'Nhập phí POS (%)...'),
      filteredValue: filteredInfo.feeApplied || null,
    },
    {
      title: 'Số tiền thực nhận',
      dataIndex: 'balance',
      key: 'balance',
      width: 140,
      render: (balance: number) => (
        <div className="text-right">
          <div className="font-bold text-blue-600">
            {formatCurrency(balance)}
          </div>
        </div>
      ),
      ...getColumnSearchProps('balance', 'Tìm số tiền thực nhận...'),
      filteredValue: filteredInfo.balance || null,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: 'Chờ xác nhận', value: 'PENDING' },
        { text: 'Đã xác nhận', value: 'COMPLETED' },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <WithdrawalTableActions
          record={record}
          loading={loading}
          onRefresh={onRefresh}
          formatCurrency={formatCurrency}
          formatCardNumber={formatCardNumber}
          onOptimisticUpdate={onOptimisticUpdate}
        />
      ),
    },
  ];

  // Sửa lại hàm handleExport
  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một bản ghi để xuất Excel');
      return;
    }

    try {
      setExportLoading(true);
      const selectedRecords = transactions.filter((record) =>
        selectedRowKeys.includes(record.id)
      );

      await WithdrawalExcelService.exportWithdrawalTransactions({
        records: selectedRecords,
        month: selectedMonth,
        year: selectedYear,
      });

      message.success(
        `Đã xuất Excel thành công cho ${selectedRecords.length} bản ghi`
      );
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setExportLoading(false);
    }
  };

  // Thêm selectionColumn
  const selectionColumn = {
    title: 'Chọn',
    key: 'select',
    width: 60,
    render: (_: any, record: any) => (
      <Checkbox
        checked={selectedRowKeys.includes(record.id)}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys([...selectedRowKeys, record.id]);
          } else {
            setSelectedRowKeys(
              selectedRowKeys.filter((key) => key !== record.id)
            );
          }
        }}
      />
    ),
  };

  // Thêm exportColumn
  const exportColumn = {
    title: 'Xuất Excel',
    key: 'export',
    width: 100,
    fixed: 'right',
    render: () => (
      <Button
        type="primary"
        icon={<FileExcelOutlined />}
        onClick={handleExport}
        loading={exportLoading}
        disabled={selectedRowKeys.length === 0}
        size="small"
      >
        Xuất ({selectedRowKeys.length})
      </Button>
    ),
  };

  // Khi transactions thay đổi, cập nhật currentFilteredData
  React.useEffect(() => {
    setCurrentFilteredData([...transactions]);
    // eslint-disable-next-line
  }, [transactions]);

  // Logic chọn tất cả bản ghi đang được lọc (không phân trang)
  const handleSelectAllFiltered = () => {
    const filteredIds = currentFilteredData.map((record) => record.id);
    const allFilteredSelected = filteredIds.every((id) =>
      selectedRowKeys.includes(id)
    );
    if (allFilteredSelected) {
      // Nếu đã chọn hết, thì bỏ chọn các bản ghi được lọc (giữ nguyên các bản ghi khác)
      setSelectedRowKeys(
        selectedRowKeys.filter((id) => !filteredIds.includes(id))
      );
    } else {
      // Nếu chưa chọn hết, thì chọn thêm các bản ghi được lọc (giữ nguyên các bản ghi đã chọn trước đó)
      const newSelected = Array.from(
        new Set([...selectedRowKeys, ...filteredIds])
      );
      setSelectedRowKeys(newSelected);
    }
  };

  // Thêm controls cho việc chọn dòng
  const controls = (
    <div className="mb-4 flex justify-between items-center">
      <Space>
        <Checkbox
          checked={
            currentFilteredData.length > 0 &&
            currentFilteredData.every((record) =>
              selectedRowKeys.includes(record.id)
            )
          }
          indeterminate={
            currentFilteredData.length > 0 &&
            currentFilteredData.some((record) =>
              selectedRowKeys.includes(record.id)
            ) &&
            !currentFilteredData.every((record) =>
              selectedRowKeys.includes(record.id)
            )
          }
          onChange={handleSelectAllFiltered}
        >
          Chọn tất cả
        </Checkbox>
        <Text type="secondary">Đã chọn: {selectedRowKeys.length} bản ghi</Text>
      </Space>
      <Space>
        <Button
          icon={<ClearOutlined />}
          onClick={clearAllFilters}
          type="text"
          disabled={Object.keys(filteredInfo).length === 0}
        >
          Xóa bộ lọc
        </Button>
      </Space>
    </div>
  );

  return (
    <div>
      {controls}
      <Spin spinning={loading}>
        <Table
          ref={tableRef}
          columns={[selectionColumn, ...columns, exportColumn]}
          dataSource={transactions}
          rowKey="id"
          scroll={{ x: 1400 }}
          className="bg-white rounded-lg"
          size="middle"
          rowClassName={(record) =>
            record.status === 'PENDING'
              ? 'bg-yellow-50 hover:bg-yellow-100'
              : 'hover:bg-gray-50'
          }
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} của ${total} bản ghi`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </Spin>
    </div>
  );
};

export default WithdrawalTable;
